import {
    StrongoShipmentParcelHistory,
    type StrongoShipmentParcelHistoryData
} from "@/types/shipments/Strongo/StrongoShipmentParcelHistory.ts";
import {
    StrongoShipmentParcelDeliveryDateChange,
    type StrongoShipmentParcelDeliveryDateChangeData
} from "@/types/shipments/Strongo/StrongoShipmentParcelDeliveryDateChange.ts";
import {StrongoInvoice, type StrongoInvoiceData} from "@/types/invoices/StrongoInvoice.ts";
import {StrongoTransportOrder, type StrongoTransportOrderData} from "@/types/order-entry/StrongoTransportOrder.ts";

export class StrongoShipmentParcel {
    public id: number;
    public strongo_shipment_id: number | null;
    public no: string;
    public from_time: Date | null;
    public scheduled_pickup_date: Date | null;
    public pickup_date: Date | null;
    public to_time: Date | null;
    public scheduled_delivery_date: Date | null;
    public status: string;
    public color: string;
    public text_color: string;
    public description: string;
    public last_status_date: Date | null;
    public hub: string;
    public from_name: string;
    public from_address: string;
    public from_city: string;
    public from_county: string;
    public to_name: string;
    public to_address: string;
    public to_city: string;
    public to_county: string;
    public type: string;
    public weight: number;
    public to_phone: number;
    public from_phone: number;
    public width: number;
    public length: number;
    public height: number;
    public comments: string;
    public to_zipcode: string;
    public from_zipcode: string;
    public services: string;
    public customer_reference: string;
    public unload_delay_kpi: string | null;
    public department_name: string | null;
    public return_for: string;
    public strongoShipmentRelatedAwb: StrongoShipmentParcel | null;
    public strongoInvoices: StrongoInvoice[];
    public strongoShipmentParcelHistories: StrongoShipmentParcelHistory[];
    public strongoShipmentParcelDeliveryDateChanges: StrongoShipmentParcelDeliveryDateChange[];
    public transportOrder: StrongoTransportOrder | null;

    constructor(data: StrongoShipmentParcelData | void) {
        if (data === undefined) {
            this.id = 0;
            this.strongo_shipment_id = null;
            this.no = "";
            this.from_time = null;
            this.scheduled_pickup_date = null;
            this.pickup_date = null;
            this.to_time = null;
            this.scheduled_delivery_date = null;
            this.status = "";
            this.color = "";
            this.text_color = "";
            this.description = "";
            this.last_status_date = null;
            this.hub = "";
            this.from_name = "";
            this.from_address = "";
            this.from_city = "";
            this.from_county = "";
            this.to_name = "";
            this.to_address = "";
            this.to_city = "";
            this.to_county = "";
            this.type = "";
            this.weight = 0;
            this.to_phone = 0;
            this.from_phone = 0;
            this.width = 0;
            this.length = 0;
            this.height = 0;
            this.comments = '';
            this.to_zipcode = '';
            this.from_zipcode = '';
            this.services = '';
            this.customer_reference = "";
            this.unload_delay_kpi = null;
            this.department_name = null;
            this.return_for = "";
            this.strongoShipmentRelatedAwb = null;
            this.strongoInvoices = [];
            this.strongoShipmentParcelHistories = [];
            this.strongoShipmentParcelDeliveryDateChanges = [];
            this.transportOrder = null;
        } else {
            this.id = data.id;
            this.strongo_shipment_id = data.strongo_shipment_id;
            this.no = data.no;
            this.from_time = null;
            if (data.from_time !== null) {
                this.from_time = new Date(data.from_time);
            }
            this.scheduled_pickup_date = null;
            if (data.scheduled_pickup_date !== null) {
                this.scheduled_pickup_date = new Date(data.scheduled_pickup_date);
            }
            this.pickup_date = null;
            if (data.pickup_date !== null) {
                this.pickup_date = new Date(data.pickup_date);
            }
            this.to_time = null;
            if (data.to_time !== null) {
                this.to_time = new Date(data.to_time);
            }
            this.scheduled_delivery_date = null;
            if (data.scheduled_delivery_date !== null) {
                this.scheduled_delivery_date = new Date(data.scheduled_delivery_date);
            }
            this.status = data.status;
            this.color = data.color;
            this.text_color = data.text_color;
            this.description = data.description;
            this.last_status_date = null;
            if (data.last_status_date !== null) {
                this.last_status_date = new Date(data.last_status_date);
            }
            this.hub = data.hub;
            this.from_name = data.from_name;
            this.from_address = data.from_address;
            this.from_city = data.from_city;
            this.from_county = data.from_county;
            this.to_name = data.to_name;
            this.to_address = data.to_address;
            this.to_city = data.to_city;
            this.to_county = data.to_county;
            this.type = data.type;
            this.weight = data.weight;
            this.to_phone = data.to_phone;
            this.from_phone = data.from_phone;
            this.width = data.width;
            this.length = data.length;
            this.height = data.height;
            this.comments = data.comments;
            this.to_zipcode = data.to_zipcode;
            this.from_zipcode = data.from_zipcode;
            this.services = data.services;
            this.customer_reference = data.customer_reference;
            this.unload_delay_kpi = data.unload_delay_kpi ?? null;
            this.department_name = data.department_name ?? null;
            this.return_for = data.return_for;
            this.strongoShipmentParcelHistories = [];
            if (data.strongoShipmentParcelHistories !== undefined) {
                data.strongoShipmentParcelHistories.forEach(strongoShipmentParcelHistory => this.strongoShipmentParcelHistories.push((new StrongoShipmentParcelHistory(strongoShipmentParcelHistory))));
            }
            this.strongoShipmentParcelDeliveryDateChanges = [];
            if (data.strongoShipmentParcelDeliveryDateChanges !== undefined) {
                data.strongoShipmentParcelDeliveryDateChanges.forEach(strongoShipmentParcelDeliveryDateChange => this.strongoShipmentParcelDeliveryDateChanges.push((new StrongoShipmentParcelDeliveryDateChange(strongoShipmentParcelDeliveryDateChange))));
            }
            this.strongoShipmentRelatedAwb = null;
            if (data.strongoShipmentRelatedAwb !== undefined) {
                this.strongoShipmentRelatedAwb = data.strongoShipmentRelatedAwb;
            }
            this.strongoInvoices = [];
            if (data.strongoInvoices !== undefined) {
                data.strongoInvoices.forEach(strongoInvoice => this.strongoInvoices.push((new StrongoInvoice(strongoInvoice))));
            }
            this.transportOrder = null;
            if (data.transportOrder !== undefined && data.transportOrder !== null) {
                this.transportOrder = new StrongoTransportOrder(data.transportOrder);
            }
        }
    }
}

export type StrongoShipmentParcelData = {
    id: number;
    strongo_shipment_id: number | null;
    no: string;
    from_time: Date | null;
    scheduled_pickup_date: Date | null;
    pickup_date: Date | null;
    to_time: Date | null;
    scheduled_delivery_date: Date | null;
    status: string;
    color: string;
    text_color: string;
    description: string;
    last_status_date: Date | null;
    hub: string;
    from_name: string;
    from_address: string;
    from_city: string;
    from_county: string;
    to_name: string;
    to_address: string;
    to_city: string;
    to_county: string;
    type: string;
    weight: number;
    to_phone: number;
    from_phone: number;
    width: number;
    length: number;
    height: number;
    comments: string;
    to_zipcode: string;
    from_zipcode: string;
    services: string;
    customer_reference: string;
    unload_delay_kpi: string | null;
    department_name: string | null;
    return_for: string;
    strongoShipmentRelatedAwb: StrongoShipmentParcelData | null | undefined;
    strongoInvoices: StrongoInvoiceData[]| undefined;
    strongoShipmentParcelHistories: StrongoShipmentParcelHistoryData[] | undefined;
    strongoShipmentParcelDeliveryDateChanges?: StrongoShipmentParcelDeliveryDateChangeData[] | undefined;
    transportOrder: StrongoTransportOrderData | null | undefined;

}